import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { Bolt } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ontologyGroupStyles, type OntologyGroup } from '@/frontend/features/Fde/ontologyGraphGroups'

export interface OntologySchemaNodeData extends Record<string, unknown> {
  name: string
  kind: 'class' | 'family' | 'interface'
  group: OntologyGroup
  source: 'baseline' | 'project'
  propertyCount: number
  elementCount: number
  linkCount: number
  actionCount: number
  backingCount: number
  active: boolean
  dimmed: boolean
  onOpenElements?: () => void
  onOpenData?: () => void
  labels: { objectType: string; family: string; ontologyInterface: string; properties: string; elements: string; classes: string; links: string; actions: string; backing: string; openData: string; projectExtension: string }
}

export type OntologySchemaFlowNode = Node<OntologySchemaNodeData, 'ontologySchema'>

export default function OntologySchemaNode({ data }: NodeProps<OntologySchemaFlowNode>) {
  const colors = ontologyGroupStyles[data.group]
  return (
    <div className={cn(
      'relative flex size-full overflow-hidden rounded-xl border bg-background shadow-sm transition-[opacity,border-color,box-shadow,background-color] duration-200',
      data.kind === 'interface' && 'border-dashed', data.active ? `ring-4 ${colors.selected}` : 'border-border', data.dimmed && 'opacity-20',
    )} data-schema-kind={data.kind}>
      <Handle type="target" position={Position.Left} className="!size-1 !border-0 !bg-transparent !opacity-0" />
      <span className={cn('pointer-events-none absolute inset-0', colors.tint)} />
      <span className={cn('relative z-10 w-1 shrink-0', colors.accent)} />
      <div className="relative z-10 flex min-w-0 flex-1 flex-col justify-center px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn('size-2 shrink-0 rounded-full', colors.dot)} />
          <p className="min-w-0 flex-1 truncate text-xs font-semibold" title={data.name}>{data.name}</p>
          {data.source === 'project' && <span className="size-1.5 shrink-0 rounded-full bg-primary" title={data.labels.projectExtension} />}
        </div>
        <p className="mt-1 truncate text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {data.kind === 'interface' ? data.labels.ontologyInterface : data.kind === 'family' ? data.labels.family : data.labels.objectType}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[9px] tabular-nums text-muted-foreground">
          <span>{data.propertyCount} {data.labels.properties}</span>
          {data.kind === 'class' && data.onOpenElements
            ? <button type="button" className="font-medium text-foreground hover:underline" onClick={(event) => { event.stopPropagation(); data.onOpenElements?.() }}>{data.elementCount} {data.labels.elements.toLocaleLowerCase()}</button>
            : <span>{data.elementCount} {data.labels.classes.toLocaleLowerCase()}</span>}
          {data.linkCount > 0 && <span>{data.linkCount} {data.labels.links}</span>}
          {data.actionCount > 0 && <span className="ml-auto flex items-center gap-1" title={data.labels.actions}><Bolt className="size-3 text-primary" />{data.actionCount}</span>}
          {data.backingCount > 0 && data.onOpenData && <button type="button" title={data.labels.backing} className="font-medium text-emerald-700 hover:underline" onClick={(event) => { event.stopPropagation(); data.onOpenData?.() }}>{data.labels.openData}</button>}
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="!size-1 !border-0 !bg-transparent !opacity-0" />
    </div>
  )
}
